import { ElMessage, messageType, ElMessageBox } from "element-plus";
import Swal, { SweetAlertIcon, SweetAlertResult } from "sweetalert2";
import { h, createApp } from "vue";
import { ArrayWrap } from "@/mixins";
import type { DefineComponent } from "vue";
import { MessageType } from "@/models/message-type";
import {
  InfoFilled,
  SuccessFilled,
  WarningFilled,
  CircleCloseFilled,
  QuestionFilled,
} from "@element-plus/icons-vue";
import { escapeHtml } from "@vue/shared";

export enum DialogType {
  empty = "empty",
  error = "error",
  info = "info",
  question = "question",
  success = "success",
  warning = "warning",
}
export enum NotificationMethod {
  message = "message",
  messageBox = "messageBox",
  none = "none",
}
const DialogTypeMap: ArrayWrap<{ type: MessageType; icon?: DefineComponent }> = {
  [DialogType.error]: { type: "error", icon: CircleCloseFilled as DefineComponent },
  [DialogType.info]: { type: "info", icon: InfoFilled as DefineComponent },
  [DialogType.question]: { type: "info", icon: QuestionFilled as DefineComponent },
  [DialogType.success]: { type: "success", icon: SuccessFilled as DefineComponent },
  [DialogType.warning]: { type: "warning", icon: WarningFilled as DefineComponent },
  [DialogType.empty]: { type: undefined },
};
function toHtmlString(component: DefineComponent) {
  const tempApp = createApp({
    render() {
      return h(component);
    },
  });
  const el = document.createElement("div");
  const mountedApp = tempApp.mount(el);
  return mountedApp.$el.outerHTML;
}

const icons: Map<SweetAlertIcon | undefined, string> = new Map();
icons.set("error", toHtmlString(CircleCloseFilled));
icons.set("info", toHtmlString(InfoFilled));
icons.set("question", toHtmlString(QuestionFilled));
icons.set("success", toHtmlString(SuccessFilled));
icons.set("warning", toHtmlString(WarningFilled));

export interface DialogMixin {
  showMessage(message: string, type?: messageType, preventClose?: boolean): void;
  showError(message?: string, preventClose?: boolean): void;
  showConfirmDialog(
    title: string,
    htmlMessage: string,
    icon?: SweetAlertIcon,
    confirmButtonText?: string
  ): Promise<SweetAlertResult<any>>;
  showNotificationDialog(
    title: string,
    htmlMessage: string,
    icon?: SweetAlertIcon,
    confirmButtonText?: string
  ): Promise<SweetAlertResult<any>>;
  showAdaptiveMessage(text: string, dialogType: DialogType, notificationMethod: NotificationMethod): void;
}

export const dialogMixins: DialogMixin = {
  showMessage(message, type?, preventClose?) {
    ElMessage({
      dangerouslyUseHTMLString: true,
      message: message,
      type: type ?? "success",
      duration: preventClose ? 0 : undefined,
      showClose: true,
      grouping: true,
    });
  },
  showError(message?, preventClose?) {
    ElMessage({
      dangerouslyUseHTMLString: true,
      message: message ?? "Сервис временно недоступен, повторите попытку позже",
      type: "error",
      duration: preventClose ? 0 : undefined,
      showClose: true,
      grouping: true,
    });
  },
  showConfirmDialog(title, htmlMessage, icon?, confirmButtonText?): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: title || " ",
      icon: icon,
      iconHtml: icons.get(icon),
      html: htmlMessage,
      showCancelButton: true,
      confirmButtonText: confirmButtonText ?? "Подтвердить",
      cancelButtonText: "Отмена",
      buttonsStyling: false,
      returnFocus: false,
      heightAuto: false,
      customClass: {
        confirmButton: "el-button el-button--primary is-plain",
        cancelButton: "el-button",
        icon: "el-icon",
      },
    });
  },
  showNotificationDialog(title, htmlMessage, icon?, confirmButtonText?) {
    return Swal.fire({
      title: title || " ",
      icon: icon,
      iconHtml: icons.get(icon),
      html: htmlMessage,
      confirmButtonText: confirmButtonText ?? "Подтвердить",
      buttonsStyling: false,
      returnFocus: false,
      heightAuto: false,
      customClass: {
        confirmButton: "el-button el-button--primary is-plain",
        icon: "el-icon",
      },
    });
  },
  showAdaptiveMessage(text: string, dialogType: DialogType, notificationMethod: NotificationMethod) {
    const html = getAdaptiveMessageHtml(text);
    switch (notificationMethod) {
      case NotificationMethod.message:
        ElMessage({
          message: html,
          type: DialogTypeMap[dialogType].type,
          duration: dialogType === DialogType.error ? 30000 : 7000,
          showClose: true,
          grouping: true,
          dangerouslyUseHTMLString: true,
        });
        return;
      case NotificationMethod.messageBox:
        ElMessageBox({
          message: html,
          dangerouslyUseHTMLString: true,
          type: DialogTypeMap[dialogType].type,
          icon: DialogTypeMap[dialogType].icon,
        });
        return;
    }
  },
};
const getAdaptiveMessageHtml = (text: string): string => {
  // \n не отображается в сообщении, поэтому заменяем его на <br />.
  return escapeHtml(text).replaceAll("\n", "<br />");
};
export default {
  methods: dialogMixins,
};
